/** sessionStorage flag so a missing hashed file cannot reload-loop. */
export const STALE_ASSET_RELOAD_STORAGE_KEY = 'nodon-stale-asset-reload';

const HASHED_ASSET_PATH = '/assets/';

export type StaleAssetReloadOptions = {
  storage?: Storage;
  reload?: () => void;
  /**
   * Document readiness when registering. Defaults to `document.readyState`.
   * `complete` means bootstrap resource errors already fired — do not clear the guard.
   */
  documentReadyState?: DocumentReadyState;
};

/**
 * True when `url` points at a content-hashed Vite build file under `/assets/`.
 * Pathname only — a query or hash containing `/assets/` does not match.
 */
export const isHashedAssetUrl = (url: string): boolean => {
  try {
    return new URL(url, window.location.origin).pathname.includes(
      HASHED_ASSET_PATH,
    );
  } catch {
    return false;
  }
};

/**
 * True when a resource-error target is a script or stylesheet from `/assets/`.
 * Used after a deploy when stale HTML still requests removed hashed files.
 */
export const isHashedAssetLoadTarget = (
  target: EventTarget | null,
): boolean => {
  if (target instanceof HTMLScriptElement) {
    return isHashedAssetUrl(target.src);
  }
  if (target instanceof HTMLLinkElement) {
    return isHashedAssetUrl(target.href);
  }
  return false;
};

/**
 * Consumes the one-shot reload guard. Returns false if this tab already reloaded.
 */
export const consumeStaleAssetReloadGuard = (storage: Storage): boolean => {
  try {
    if (storage.getItem(STALE_ASSET_RELOAD_STORAGE_KEY)) return false;
    storage.setItem(STALE_ASSET_RELOAD_STORAGE_KEY, '1');
    return true;
  } catch {
    return false;
  }
};

/**
 * Drops the one-shot reload guard so a later deploy in this tab can recover again.
 * Call after the document has loaded without a hashed-asset error
 * (see {@link registerStaleAssetReload}).
 */
export const clearStaleAssetReloadGuard = (storage: Storage): void => {
  try {
    storage.removeItem(STALE_ASSET_RELOAD_STORAGE_KEY);
  } catch {
    return;
  }
};

/**
 * Reloads once so the next document fetch can pick up a new `index.html`.
 * No-ops if {@link consumeStaleAssetReloadGuard} has already fired in this tab.
 */
export const reloadOnceOnStaleAsset = (
  options: StaleAssetReloadOptions = {},
): boolean => {
  const storage = options.storage ?? sessionStorage;
  if (!consumeStaleAssetReloadGuard(storage)) return false;
  const reload = options.reload ?? (() => window.location.reload());
  reload();
  return true;
};

/**
 * Reloads when Vite fails to preload a lazy chunk or a hashed script/CSS 404s.
 * Calls `preventDefault` on `vite:preloadError` so Vite does not rethrow after recovery.
 * After `load`, clears {@link STALE_ASSET_RELOAD_STORAGE_KEY} if no hashed-asset
 * error was seen, so a later deploy in this tab can recover again.
 * Does not clear when the document is already `complete` — bootstrap errors may already have fired.
 */
export const registerStaleAssetReload = (
  target: Pick<Window, 'addEventListener'> = window,
  options: StaleAssetReloadOptions = {},
): void => {
  const storage = options.storage ?? sessionStorage;
  const documentReadyState = options.documentReadyState ?? document.readyState;

  let sawStaleAssetError = false;

  const onStale: EventListener = () => {
    sawStaleAssetError = true;
    reloadOnceOnStaleAsset(options);
  };

  target.addEventListener('vite:preloadError', (event) => {
    // Vite rethrows unless default is prevented — that would hit ErrorBoundary / Sentry.
    event.preventDefault();
    onStale(event);
  });
  target.addEventListener(
    'error',
    (event) => {
      if (!isHashedAssetLoadTarget(event.target)) return;
      onStale(event);
    },
    true,
  );

  // Resource errors fire before `load`. Clearing on register re-enables a loop
  // when a hashed CSS/chunk/font keeps failing after the main bundle runs.
  if (documentReadyState === 'complete') return;

  target.addEventListener(
    'load',
    () => {
      if (sawStaleAssetError) return;
      clearStaleAssetReloadGuard(storage);
    },
    { once: true },
  );
};
